/**
 * P5 (Juni 2026) – Zentrales Mapping der serverseitigen P2-Policy-Fehler
 * (HTTP 403 mit stabilem `code`) auf verständliche, deutschsprachige
 * Nutzertexte für Handwerksbetriebe.
 *
 * Reines Frontend-Mapping. KEINE clientseitige Sicherheitslogik: die Sperre
 * bleibt serverseitig (P2). Diese Datei erklärt dem Nutzer nur, WARUM eine
 * Aktion blockiert wurde – sie entscheidet nichts.
 *
 * Der `code` kommt aus `response.data.code` (Backend-Handler für
 * AccountPolicyError) bzw. ersatzweise `response.data.detail.code`.
 */

const GENERIC = {
  variant: "danger",
  title: "Zugang konnte nicht geprüft werden",
  text:
    "Die Plattform konnte den Status dieses Zugangs nicht eindeutig prüfen. " +
    "Bitte wenden Sie sich an den Plattformbetreiber.",
  cta: "Betreiber kontaktieren",
};

// BUCH-01 (Betreiberfreigabe 12.10.): Ziel aller „Abo abschließen"-Hinweise = Tarifkarte.
export const ABO_TARGET = "/unternehmer/einstellungen#tarif";

// code → { variant, title, text, cta }. Texte exakt gemäß P5-Vorgabe.
const POLICY_MESSAGES = {
  read_only_expired: {
    variant: "warning",
    title: "Ihre Demo-Version ist abgelaufen",
    text:
      "Sie können bestehende Nachweise und die Prüfmappe weiterhin ansehen. " +
      "Neue Mitarbeiter, Welten, Aufgaben und Nachweise können erst nach " +
      "Freischaltung wieder erstellt werden.",
    // BUCH-01: CTA „Zugang verlängern lassen" → „Abo abschließen" mit Ziel Tarifkarte.
    cta: "Abo abschließen",
    cta_href: ABO_TARGET,
  },
  read_only_suspended: {
    variant: "danger",
    title: "Dieser Zugang wurde vorübergehend gesperrt",
    text:
      "Ihr Zugang ist aktuell auf Lesen beschränkt. Bitte wenden Sie sich an " +
      "den Plattformbetreiber.",
    cta: "Betreiber kontaktieren",
  },
  limit_employees: {
    variant: "warning",
    title: "Mitarbeiterlimit erreicht",
    text:
      "Ihr aktueller Tarif erlaubt nur eine begrenzte Anzahl aktiver " +
      "Mitarbeiter. Für weitere Mitarbeiter benötigen Sie den nächsthöheren " +
      "Tarif. Eine Übersicht finden Sie unter „Einstellungen → Tarif & Abrechnung\".",
    cta: "Tarif & Abrechnung ansehen",
  },
  limit_worlds: {
    variant: "warning",
    title: "Weltenlimit erreicht",
    text:
      "Für diesen Zugang können aktuell keine weiteren Dokumentationswelten " +
      "erzeugt werden.",
    cta: "Zugang erweitern lassen",
  },
  limit_ai: {
    variant: "warning",
    title: "KI-Kontingent erreicht",
    text:
      "Das KI-Kontingent dieses Zugangs ist aufgebraucht. Bestehende Inhalte " +
      "bleiben weiterhin verfügbar.",
    cta: "Zugang erweitern lassen",
  },
  // Fail-closed-Codes → bewusst generische, beruhigende Meldung.
  status_invalid: GENERIC,
  company_missing: GENERIC,
  no_tenant: GENERIC,
  limit_invalid: GENERIC,
  // FIX-01b-2 (Betreiberfreigabe 12.10., E9): offene Zahlung – wortgleich.
  // ZAHL-02b (Betreiberfreigabe 13.10., Soll 6): neuer Wortlaut, Ziel Tarifkarte („Zahlungsdaten ändern").
  read_only_license_inaktiv: {
    variant: "warning",
    title: "Offene Zahlung",
    text:
      "Ihr Konto ist wegen einer offenen Zahlung eingeschränkt. Bestehende Inhalte und Nachweise " +
      "bleiben verfügbar. Sobald die Zahlung eingegangen ist, wird Ihr Zugang automatisch wieder freigeschaltet.",
    cta: "Zahlungsdaten ändern",
    cta_href: "/unternehmer/einstellungen#tarif",
  },
  // ZAHL-02d (Betreiberfreigabe 14.10., D4/D5): Vertragsende – Lesephase bzw. Sperre (Text wie Backend-detail).
  read_only_vertragsende: {
    variant: "warning",
    title: "Vertrag beendet",
    text:
      "Ihr CLARTEXT-Vertrag ist beendet. Bis zum Ende der Lesephase können Sie alle Inhalte ansehen und " +
      "Ihre Nachweise exportieren; schreibende Aktionen sind gesperrt.",
    cta: "Abo neu abschließen",
    cta_href: ABO_TARGET,
  },
  gesperrt_vertragsende: {
    variant: "danger",
    title: "Zugang beendet",
    text: "Ihr CLARTEXT-Zugang ist beendet.",
    cta: "Abo neu abschließen",
    cta_href: ABO_TARGET,
  },
};

// ZAHL-02d (Betreiberfreigabe 14.10., Soll 5): Vertragsende – Texte wortgleich, Daten TT.MM.JJJJ aus dem Backend
// (`access_state.vertragsende`: vertragsende_text, lesephase_bis_text). Ziel „Nachweise exportieren" = BG Prüfmappe (V5).
export const EXPORT_TARGET = "/unternehmer/pruefmappe";
export const CHECKOUT_FEHLER_TEXT =
  "Der Abo-Abschluss konnte nicht gestartet werden. Bitte versuchen Sie es später erneut.";

export function vertragsendeBannerText(ve) {
  return (
    `Ihr CLARTEXT-Vertrag ist am ${ve?.vertragsende_text || "–"} ausgelaufen. Bis zum ${ve?.lesephase_bis_text || "–"} ` +
    "können Sie alle Inhalte ansehen und Ihre Nachweise exportieren. Danach ist der Zugang gesperrt, bis Sie ein neues Abo abschließen."
  );
}

export function vertragsendeTarifText(ve) {
  return `Vertrag beendet am ${ve?.vertragsende_text || "–"}. Lesezugriff bis ${ve?.lesephase_bis_text || "–"}.`;
}

export function vertragsendeSperrText(ve) {
  return (
    `Ihr CLARTEXT-Zugang ist beendet. Der Vertrag ist am ${ve?.vertragsende_text || "–"} ausgelaufen, die Lesephase endete am ` +
    `${ve?.lesephase_bis_text || "–"}. Ihre Daten sind weiterhin gespeichert. Mit einem neuen Abo steht Ihnen alles sofort wieder zur Verfügung.`
  );
}

// Bestehender Abo-Checkout (POST /api/billing/checkout, nur origin_url) → Weiterleitung auf die Stripe-Session (D6).
export async function startAboCheckout(apiClient) {
  const res = await apiClient.createCheckout(window.location.origin);
  if (res && res.url) window.location.assign(res.url);
  return res;
}

export function checkoutErrorText(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.code === "angebot_erforderlich") {
    return "Ab 51 Mitarbeitern erstellen wir Ihnen ein individuelles Angebot – bitte wenden Sie sich an info@clartext.io.";
  }
  if (detail && typeof detail === "object" && typeof detail.message === "string" && detail.message) return detail.message;
  return CHECKOUT_FEHLER_TEXT;
}

// ZAHL-02b (Betreiberfreigabe 13.10., Soll 6): Texte der Zahlungs-Banner (Owner), wortgleich.
export const ZAHLUNG_OFFEN_TEXT =
  "Die letzte Zahlung konnte nicht eingezogen werden. Bitte aktualisieren Sie Ihre Zahlungsdaten, " +
  "damit Ihr Zugang nicht eingeschränkt wird.";
export const OFFENE_ZAHLUNG_LESEZUGRIFF_TEXT =
  "Ihr Konto ist wegen einer offenen Zahlung eingeschränkt. Bestehende Inhalte und Nachweise bleiben " +
  "verfügbar. Sobald die Zahlung eingegangen ist, wird Ihr Zugang automatisch wieder freigeschaltet.";
export const PORTAL_FEHLER_TEXT =
  "Die Zahlungsverwaltung ist gerade nicht erreichbar. Bitte versuchen Sie es später erneut.";

// Öffnet das Stripe-Kundenportal in derselben Registerkarte (ziel "zahlungsdaten" | "rechnungen").
export async function openBillingPortal(apiClient, ziel) {
  const res = await apiClient.createPortalSession(ziel, window.location.origin);
  if (res && res.url) window.location.assign(res.url);
  return res;
}

export function portalErrorText(err) {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : PORTAL_FEHLER_TEXT;
}

// FIX-01b-2 (E9): Mitarbeitertext für JEDEN read_only_*-Grund außer suspended
// (dort bleibt das bestehende Verhalten). Wortgleich gemäß Betreibervorgabe 12.10.
export const EMPLOYEE_READ_ONLY_TEXT =
  "Ihr Betrieb nutzt CLARTEXT derzeit im Lesezugriff. Bereits zugewiesene Unterweisungen können Sie weiterhin abschließen.";

export function isEmployeeReadOnlyCode(code) {
  return typeof code === "string" && code.startsWith("read_only_") && code !== "read_only_suspended";
}

// WELT-01b (Okt 2026): Welten-Kontingent erschöpft (HTTP 409). Text je nach
// Einstufung (aus `response.data.detail.klassifikation`). Verbindlicher
// Wortlaut gemäß Betreibervorgabe 11.10.2026.
const WORLD_QUOTA_TEXTS = {
  testkunde: {
    title: "Welten-Kontingent erreicht",
    text:
      "Sie haben die 5 eigenen Welten der Testphase genutzt. Mit dem Abo " +
      "stehen Ihnen insgesamt 10 eigene Welten zur Verfügung.",
    cta: "Abo abschließen",
    // BUCH-01b (Betreiberfreigabe 12.10.): gleiches Ziel wie BUCH-01 (Tarifkarte).
    cta_href: ABO_TARGET,
  },
  zahlkunde: {
    title: "Welten-Kontingent erreicht",
    text:
      "Sie haben Ihre 10 inklusiven eigenen Welten genutzt. Weitere eigene " +
      "Welten erhalten Sie mit einem Welten-Paket (10 Welten für 79 € netto).",
    cta: "Welten-Paket ansehen",
    // WELT-02b (Betreiberfreigabe 12.10.): Zahlkunde kann direkt ein Paket kaufen.
    paketPurchase: true,
  },
};

function buildWorldQuotaMessage(error) {
  const detail = error?.response?.data?.detail;
  const klass = (detail && detail.klassifikation) || "testkunde";
  const meta = WORLD_QUOTA_TEXTS[klass] || WORLD_QUOTA_TEXTS.testkunde;
  return { code: "world_quota_exhausted", variant: "warning", ...meta };
}

function extractPolicyCode(error) {
  const data = error?.response?.data;
  if (!data) return undefined;
  if (typeof data.code === "string" && data.code) return data.code;
  if (data.detail && typeof data.detail === "object" && typeof data.detail.code === "string") {
    return data.detail.code;
  }
  return undefined;
}

/**
 * Wertet einen (axios-)Fehler aus. Liefert ein normalisiertes Banner-Objekt
 * `{ code, variant, title, text, cta }` ODER `null`, wenn es KEIN
 * Account-Policy-Fehler ist (dann bleibt der bisherige Fehlerpfad zuständig).
 *
 * - kein 403 → null (unerwartete Fehler bleiben im bisherigen Pfad)
 * - 403 ohne Policy-`code` → null (kein P2-Fehler, z. B. anderer Guard)
 * - 403 mit bekanntem code → spezifische Meldung
 * - 403 mit unbekanntem code → generische, verständliche Meldung
 */
export function normalizePolicyError(error, options = {}) {
  const status = error?.response?.status;
  // WELT-01b: Welten-Kontingent ist ein fachlicher 409 (kein 403). Alle übrigen
  // Policy-Sperren bleiben 403.
  if (status !== 403 && status !== 409) return null;
  const code = extractPolicyCode(error);
  if (!code) return null;
  if (code === "world_quota_exhausted") {
    return buildWorldQuotaMessage(error);
  }
  if (status !== 403) return null;
  // FIX-01b-2 (E9): Mitarbeiter sehen bei read_only_* (außer suspended) den
  // rollenspezifischen Text – der Unternehmertext bleibt unverändert.
  if (options.role === "employee" && isEmployeeReadOnlyCode(code)) {
    return { code, variant: "warning", title: "Lesezugriff", text: EMPLOYEE_READ_ONLY_TEXT, cta: null };
  }
  const meta = POLICY_MESSAGES[code] || GENERIC;
  return { code, ...meta };
}

export function isAccountPolicyError(error) {
  return normalizePolicyError(error) !== null;
}

export function getAccountPolicyMessage(error) {
  return normalizePolicyError(error);
}
